import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import lodash from 'lodash'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { SHELL_SIZE_KEYS } from 'components/Editor/types/data.constants'
import { DeviceMode, PreviewMode } from 'services/Store/Project/enums'
import { useUrlByUsage } from 'utils/files'

import ResponsiveImage from '../../ResponsiveImage'
import { useElementCompleted } from '../../hooks/useElementCompleted'
import { useElementRootStyles } from '../../hooks/useElementRootStyles'
import LabeledGraphicContext from './LabeledGraphicContext'
import * as s from './LabeledGraphicElement.module.scss'
import MobileView from './MobilePointsView'
import PdfComponent from './PfdComponent'
import Point from './Point'
import type { LabeledGraphicElementType } from './labeledGraphicTypes'

const LabeledGraphicElement: LabeledGraphicElementType = ({
  mode,
  element,
  state,
  setState,
  styles,
  block,
  waiting,
  onChange,
  font,
}) => {
  const [viewedPoints, setViewedPoints] = useState<Record<string, boolean>>(
    element.value.items.reduce((acc, item) => ({ ...acc, [item.value]: false }), {}),
  )

  const isMobile = mode.deviceMode === DeviceMode.mobile
  const allViewed = lodash.every(viewedPoints)
  const image = element.value.image

  const ariaLabelText = `Active labels group of ${element.value.items.length} elements`

  const setActivePoint = useCallback(
    (i: string | null) => {
      setState?.({ activePoint: i, incomplete: !allViewed })
      if (i !== null && !viewedPoints[i]) {
        setViewedPoints((prev) => ({ ...prev, [i]: true }))
      }
    },
    [allViewed, setState, viewedPoints],
  )

  const imgRef = useRef<HTMLImageElement>(null)
  const fileUrl = useUrlByUsage(element.value.image, FileUsageTypeEnum.image)

  const shellStyles = {
    maxWidth: '100%',
    maxHeight: '100%',
    ...lodash.pick(styles.shell, SHELL_SIZE_KEYS),
    ...(element.value.image?.params?.round && { borderRadius: '50%' }),
  }

  const rootStyles = useElementRootStyles(styles.root, shellStyles)
  const imageStyles = useElementRootStyles(shellStyles, styles.effects, styles.image)

  useElementCompleted({
    block,
    elementId: element.id,
    completed: allViewed,
    deviceMode: mode.deviceMode,
  })

  const isPdf = mode.previewMode === PreviewMode.pdf

  const contextValue = useMemo(
    () => ({
      block,
      element,
      imgRef,
      mode,
      onChange,
      setState,
      styles,
      waiting,
      activePoint: state?.activePoint,
      setActivePoint,
    }),
    [block, element, onChange, imgRef, mode, setState, styles, waiting, state, setActivePoint],
  )

  if (isPdf) {
    return (
      <LabeledGraphicContext.Provider value={contextValue}>
        <PdfComponent
          filePath={fileUrl}
          font={font}
          styles={{ allStyles: styles, rootStyles, imageStyles }}
        />
      </LabeledGraphicContext.Provider>
    )
  }

  return (
    <LabeledGraphicContext.Provider value={contextValue}>
      <section
        aria-label={ariaLabelText}
        className={s.root}
        ref={imgRef}
        style={rootStyles}
      >
        <ResponsiveImage
          overlay={element.value.overlay}
          style={{ ...imageStyles, objectFit: image?.objectFit }}
          usage={element.value.image}
        />

        {isMobile && element.value.items.length ? (
          <MobileView font={font} viewedPoints={viewedPoints} />
        ) : (
          element.value.items?.map((item, index) => (
            <Point
              font={font}
              id={element.id}
              index={index}
              isActive={state?.activePoint === item.value}
              isAnimated={styles.animation.pulse && !viewedPoints[item.value]}
              key={item.value}
              point={item}
              value={element.value}
            />
          ))
        )}
      </section>
    </LabeledGraphicContext.Provider>
  )
}

export default React.memo(LabeledGraphicElement)
